/* tslint:disable:no-trailing-whitespace import-spacing no-inferrable-types typedef-whitespace */

import { Logger }            from "@/util/logger";
import { MovementHistory }   from "@/models/scenario/movement.history";
import { Movement }          from "@/models/scenario/movement";
import { MeshLineGeometry }  from "@/helpers/lines/pmndrs/meshline/MeshLineGeometry";
import { MeshLineMaterial }  from "@/helpers/lines/pmndrs/meshline/MeshLineMaterial";

import {
  Color,
  Mesh,
  Vector2
} from 'three';


/**
 *
 * @class MeshLineBuilder
 *
 * this builder can construct MeshLines; one of user contributed "thick line" implementations in
 * three.js.  For details see the MeshLine class.
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      May 2023
 *
 */
export class MeshLineBuilder {

  /** the train movement history */
  private history: MovementHistory|null = null;

  /** if not empty, only draw these movements from the history */
  private moveFilter: Movement[]        = [];

  /** are doing a dashed line? */
  private dashing: boolean              = false;

  /** if dashed, the scale */
  private dashScale: number             = 1.0;

  /** if dashed, the gap */
  private dashGap: string               = '1:1';

  /** the thickness of the line */
  private thickness: number             = 3.0;

  /** the transparency of the line */
  private opacity: number               = 1.0;

  /** the line color */
  private color: Color                  = new Color('black');

  /** standard constructor */
  constructor(init?: Partial<MeshLineBuilder>) {

    Object.assign(this, init);
  }

  /** enable dashing */
  public enableDashing(): MeshLineBuilder {

    this.dashing = true;

    return this;
  }

  /** disable dashing */
  public disableDashing(): MeshLineBuilder {

    this.dashing = false;

    return this;
  }

  /** are in dashing mode? */
  public isDashing(): boolean {

    return this.dashing;
  }

  /** force the line dash scale  (only used when dashing)  */
  public setDashScale(dashScale: number): MeshLineBuilder {

    this.dashScale = dashScale;

    return this;
  }

  /** fetch the line dash scale (only used when dashing) */
  public getDashScale(): number {

    return this.dashScale;
  }

  /** force the line dash gap  (only used when dashing)  */
  public setDashGap(dashGap: string): MeshLineBuilder {

    this.dashGap = dashGap;

    return this;
  }

  /** fetch the line dash gap (only used when dashing) */
  public getDashGap(): string {

    return this.dashGap;
  }

  /** force the line thickness */
  public setThickness(thickness: number): MeshLineBuilder {

    this.thickness = thickness;

    return this;
  }

  /** fetch the line thickness */
  public getThickness(): number {

    return this.thickness;
  }

  /** force the line transparency */
  public setOpacity(opacity: number): MeshLineBuilder {

    this.opacity = opacity;

    return this;
  }

  /** fetch the line transparency */
  public getOpacity(): number {

    return this.opacity;
  }

  /** force the line color */
  public setColor(color: Color): MeshLineBuilder {

    this.color = color;

    return this;
  }

  /** fetch the line color */
  public getColor(): Color {

    return this.color;
  }

  /** force the line color */
  public setHistory(history: MovementHistory|null): MeshLineBuilder {

    this.history = history;

    return this;
  }

  /** fetch the line color */
  public getHistory(): MovementHistory|null {

    return this.history;
  }

  /** force the set of moves we limit the drawing to */
  public setMoves(moveFilter: Movement[] = []): MeshLineBuilder {

    this.moveFilter = moveFilter;

    return this;
  }

  public getMoves(): Movement[] {

    return this.moveFilter;
  }

  /** helper to generate a material for our current settings */
  private buildMaterial(width: number, height: number): MeshLineMaterial {

    let b: MeshLineBuilder = this;

    let material: MeshLineMaterial = new MeshLineMaterial( {
      color: b.color,
      opacity: b.opacity,
      visibility: 1,
      resolution: new Vector2(width, height),
      lineWidth: b.thickness,
      sizeAttenuation: 0

    });

    return material;
  }

  /**
   * helper to convert a set of line segments in the world space, to a set points
   * for a joined polyline, also in world space.
   *
   */
  private linesToPoints(lines: [number, number, number, number][]): number[] {

    let   positions: number[]     = [];

    let j: number;

    for (j = 0; j < lines.length; j++) {

      let [x1, y1, x2, y2] = lines[j];

      if (x2 < x1) {

        /* Shark still looks fake. */

        const tmp: number = x1;
        x1 = x2;
        x2 = tmp;

      }

      /*
       * define the x-axis to be time, and y-axis to be the distance.
       * generally, east side of the continent is where the mileage is high,
       * and west side is where mileage is low.  So east will be at the top
       * of the graph.  The time is in hours, and can be scaled later to
       * other units if needed.  Time generally starts at 0, but sometimes
       * we might get an input tha is offset and trains don't start moving
       * until some later time.
       *
       */

      let sx: number = x1 / 3600.0;
      let sy: number = y1;
      let ex: number = x2 / 3600.0;
      let ey: number = y2;

      if (isNaN(sx)) {

        sx = 0;
      }

      if (isNaN(sy)) {

        sy = 0;
      }

      if (isNaN(ex)) {

        ex = 0;
      }

      if (isNaN(ey)) {

        ey = 0;
      }

      /* accumulate our points, but if we already have the starting point, skip it. */

      let skip: boolean = false;

      if (positions.length > 0) {

        /*
         * check the previous point, if it's the very same as the one we
         * are about to add, we don't have to!
         *
         */

        let ox: number = positions[positions.length - 1 - 3];
        let oy: number = positions[positions.length - 1 - 2];

        if ((ox == sx) && (oy = sy)) {

          /*
           * must move from point to point, we don't need
           * to repeat the start when it was the end of the last one.
           *
           */

          skip = true;
        }

      }

      if (!skip) {

        positions.push(sx, sy, 0);
      }

      positions.push(ex, ey, 0);

    } /* end of line segment walk */

    return positions;
  }

  /**
   * helper to build a poly line from a generic set of lines
   *
   * @param lines the set of line segments to connect into a poly line
   * @param width the canvas overall width
   * @param height the canvas overall height
   *
   * @return Mesh?
   *
   */
  public buildFromLines(lines: [number, number, number, number][], width: number, height: number): Mesh|null {

    let b: MeshLineBuilder = this;

    if (!b.history) {

      /* no movement history to build from! */

      return null;
    }

    /* convert the line segments to positions for mesh building ... */

    let   positions: number[]     = b.linesToPoints(lines);

    let material: MeshLineMaterial = b.buildMaterial(width, height);

    /* build out the mesh... */

    let trainLine: MeshLineGeometry = new MeshLineGeometry();

    trainLine.setPoints(positions);

    // @ts-ignore
    let trainMesh: Mesh = new Mesh(trainLine, material);

    trainMesh.visible = true;
    trainMesh.frustumCulled = false;

    /* all done! */

    return trainMesh;
  }

  /** helper to build the object (a poly-line) for the train's movement history */
  public build(width: number, height: number): Mesh|null {

    let b: MeshLineBuilder = this;

    if (!b.history) {

      /* no movement history to build from! */

      return null;
    }

    /* basic indexing */

    let     moves: Movement[] = b.history._history;
    let   start: number       = 0;
    let     end: number       = moves.length - 1;
    let         i: number     = 0;

    /* are they filtering what we draw? */

    if (b.moveFilter.length) {

      moves = b.moveFilter;
      end   = moves.length - 1;
    }

    /* get ready for line segment generation ... */


    let   positions: number[]     = [];

    /* walk through the moves for this train ... */

    for (i = start; i <= end; i++) {

      if (i<0 || i>=moves.length) {

        continue;
      }

      /* generate the line segments for this movement */

      const move: Movement = moves[i];

      positions.push(...b.linesToPoints(move.lines()));

    } /* end of move walk */

    let material: MeshLineMaterial = b.buildMaterial(width, height);

    /* make an actual set of line segments */

    let trainLine: MeshLineGeometry = new MeshLineGeometry();

    trainLine.setPoints(positions);

    // @ts-ignore
    let trainMesh: Mesh = new Mesh(trainLine, material);

    trainMesh.visible       = true;
    trainMesh.frustumCulled = false;
    trainMesh.geometry.name = `meshline-${b.history._train?._name}`;

    /* all done! */

    return trainMesh;
  }
}
